<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @ok="duplicateUnit"
        >
            <template #modal-title>
                Duplicate Week
            </template>

            <div v-if="loading_publications" class="mb-3">
                <b-spinner label="Loading..." />
            </div>

            <div v-else>
                <b-form-group
                    label="Publication"
                    label-for="target_publication"
                >
                    <multiselect
                        v-model="target_publication"
                        :options="filtered_publications"
                        :class="{'is-invalid': validating && !target_publication}"
                        label="sku"
                        track-by="sku"
                        :internal-search="false"
                        @search-change="filterPublications"
                    >
                        <template v-slot:singleLabel="props">
                            <span v-if="props.option.title">{{ props.option.title }}</span>
                            <span v-else>no title</span>
                            <span>({{ props.option.sku }})</span>
                        </template>
                        <template v-slot:option="props">
                            <div class="option__desc">
                                <span class="option__title">
                                    <span v-if="props.option.title">{{ props.option.title }}</span>
                                    <span v-else>no title</span>
                                    <span>({{ props.option.sku }})</span>
                                </span>
                            </div>
                        </template>
                    </multiselect>

                    <b-form-text>
                        Select the Publication you would like to duplicate this week into
                    </b-form-text>

                    <b-form-invalid-feedback :state="validating && !target_publication ? false : null">
                        Publication is required
                    </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label-for="target_unit">
                    <label>
                        Unit
                        <span
                            v-if="unit_groups"
                            :class="{
                                'text-success': unit_groups.length > 0,
                                'text-danger': !unit_groups || unit_groups && !unit_groups.length
                            }"
                        >
                            ({{ unit_groups.length }})
                        </span>
                    </label>
                    <multiselect
                        v-model="target_unit_group"
                        :options="unit_groups"
                        :class="{'is-invalid': validating && !target_unit_group}"
                        label="title"
                        track-by="title"
                    >
                        <template v-slot:singleLabel="props">
                            <span v-if="props.option.title">{{ props.option.title }}</span>
                            <span v-else>no title</span>
                        </template>
                        <template v-slot:option="props">
                            <div class="option__desc">
                                <span class="option__title">
                                    <span v-if="props.option.title">{{ props.option.title }}</span>
                                    <span v-else>no title</span>
                                </span>
                            </div>
                        </template>
                    </multiselect>

                    <b-form-text>
                        Select the Unit you would like to duplicate this week into
                    </b-form-text>

                    <b-form-invalid-feedback :state="validating && !target_publication ? false : null">
                        Unit is required
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>

            <template #modal-footer>
                <b-button variant="light" @click="cancel">Cancel</b-button>
                <b-button variant="info" @click="duplicateUnit" :disabled="!target_publication || !target_unit_group">
                    <font-awesome-icon :icon="['fad', 'clone']" fixed-width />
                    Duplicate Week
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import {mapState} from 'vuex'
import moment from 'moment'

export default {
    data: () => ({
        unit_groups: [],
        target_publication: null,
        target_unit_group: null,
        search_term: '',
        loading_publications: false
    }),
    props: [
        'value',
        'unit',
    ],
    components: {},
    computed: {
        ...mapState({
            validating: state => state.utilities.validating,
            publications: state => state.publications.publications,
            publication: state => state.publications.publication[0],
        }),
        modal_id() {
            let modal_id = 'duplicate-unit-modal'
            return modal_id
        },
        filtered_publications() {
            return this.publications.filter(publication => {
                if (publication.locked) {
                    return false
                }

                let search = this.search_term.toLowerCase()

                let search_match = false
                if (
                    this.search_term === '' ||
                    publication.title.toLowerCase().indexOf(search) > -1 ||
                    publication.sku.toLowerCase().indexOf(search) > -1
                ) {
                    search_match = true
                }

                return search_match
            })
        }
    },
    watch: {
        value: {
            async handler(value) {
                if (!value) {
                    return
                }

                this.$store.commit('setValidating', false)
                await this.getPublications()

                if (this.publication && !this.publication.locked) {
                    this.target_publication = this.publication
                }
            }
        },
        target_publication: {
            async handler(publication) {
                // if the publication changes, we need to reset the unit group selector
                this.target_unit_group = null

                if (!publication) {
                    this.search_term = ''
                    return
                }

                try {
                    const response = await this.axios.get(`/api/unit_groups/${this.target_publication.id}`)
                    const publication = response.data.find(
                        (publication) => publication.id === this.target_publication.id
                    )
                    this.unit_groups = publication ? publication.unit_groups : []
                } catch (e) {
                    await this.$store.dispatch('pushError', {
                        data: {
                            message: `Failed to load units for publication: ${e.message}`,
                        },
                        time: moment().toISOString(),
                    })
                }
            }
        }
    },
    methods: {
        filterPublications(search_term) {
            if (search_term) {
                this.search_term = search_term
            }
        },
        async getPublications() {
            this.loading_publications = true
            await this.$store.dispatch('getPublications')
            this.loading_publications = false
        },
        async duplicateUnit() {
            this.$store.commit('setValidating', true)

            if (!this.validated()) {
                return false
            }

            const payload = {
                old_publication_id: this.publication.id,
                new_publication_id: this.target_publication.id,
                new_unit_group_id: this.target_unit_group.id,
                unit_id: this.unit.id,
            }

            try {
                await this.axios.post('/api/units/duplicate', payload)
                this.$bvToast.toast('Week duplicated', {
                    title: 'Success',
                    variant: 'success',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
                // in case we duplicated into the same publication, refresh the unit groups
                await this.$store.dispatch('getUnitGroups', this.publication.id)
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to duplicate week: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.hide()
            }
        },
        validated() {
            let validated = true

            if (!this.target_publication) {
                validated = false
            }

            if (!this.target_unit_group) {
                validated = false
            }

            return validated
        },
        cancel(event) {
            event.preventDefault()

            this.hide()
        },
        hide() {
            this.$emit('hide')
        },
        hidden() {
            this.$store.commit('setValidating', false)

            this.target_publication = null

            this.$emit('hide')
        }
    }
}
</script>
